/**
 * Pure helpers for the Required-action queue's two tabs.
 *
 * The queue is TWO tables, not one: an unposted-transaction arm and a
 * discrepancy arm, over two different wire shapes that disagree about what
 * their shared-looking slots mean (see ADR-0001). Everything per-tab —
 * columns, grid tracks, filter vocabulary, copy — is therefore defined twice
 * here rather than unified behind a lookup, so the two can never borrow each
 * other's meaning.
 *
 * Kept free of React and of the api-client (which instantiates axios at load)
 * so it unit-tests in isolation — enums come from the axios-free
 * reconciliation-v2-enums module, and the row shapes plus `PromisedAction`
 * (whose module does value-import React and the api-client) are `import type`
 * only, so they erase at compile time and pull in nothing at runtime.
 */

import dayjs from 'dayjs'

import {
  ReconIssueType,
  ReviewReason,
} from '../../../api/reconciliation-v2-enums'
import { ISSUE_TYPE_LABEL, ISSUE_TYPE_TONE } from '../recon-v2-helpers'
import type {
  ReconIssueRow,
  ReviewQueueCounts,
  ReviewQueueFilter,
} from '../../../api/reconciliation-v2-api-client'
import type { PromisedAction } from '../AdjustPaymentModal.types'

// ── columns ─────────────────────────────────────────────────────────────────

/**
 * One column of a tab: its header label AND the grid track it occupies.
 * Deliberately one object rather than a labels array beside a tracks array —
 * a column cannot then be added to the header without also giving it a track,
 * which is the drift the pre-split card list suffered from.
 *
 * `sortKey` is the seam for issue 10: while every column is label-only
 * (`ReviewQueueParams` has no `sortBy` and the backend order is fixed — spec
 * D4), no column defines one and <TableHead> renders static cells. Adding keys
 * here plus an `onToggleSort` prop turns them into sort buttons without
 * touching the markup.
 */
export interface ReviewQueueColumn {
  label: string
  /** This column's track in the tab's `grid-template-columns`. */
  track: string
  sortKey?: string
  /** Right-aligns the cell — money and other figures. */
  numeric?: boolean
  /**
   * Lets the HEADER label wrap onto a second line instead of being held on one
   * by the shared `nowrap`. For a column whose honest name is much wider than
   * its values — the two Age columns — wrapping the label is what keeps the
   * track sized for the number rather than for the sentence naming it.
   */
  wrap?: boolean
  /** Header-level explanation of what the column actually holds. */
  tooltip?: string
}

/** `grid-template-columns` for a column set. Exported for unit tests. */
export const gridTemplate = (columns: readonly ReviewQueueColumn[]): string =>
  columns.map((column) => column.track).join(' ')

// Directly after Carrier on both tabs. Sized for the shortened id the Benefits
// table also shows ("0a33...8319", 11 mono characters) plus the warning flag an
// unresolved iStream row adds and the copy button.
const BENEFIT_ID_COLUMN: ReviewQueueColumn = {
  label: 'Benefit ID',
  track: 'minmax(152px, 0.6fr)',
}

/**
 * Sum of the tracks' `minmax(<N>px, …)` minimums — the width below which the
 * grid overflows its scrollport. The header, rows and skeleton sit in a wrapper
 * this wide so, when the table scrolls sideways, their backgrounds and borders
 * span every column instead of stopping at the viewport edge. Exported for tests.
 */
export const gridMinWidth = (columns: readonly ReviewQueueColumn[]): number =>
  columns.reduce((sum, column) => {
    const min = /minmax\(\s*(\d+)px/.exec(column.track)
    return sum + (min ? Number(min[1]) : 0)
  }, 0)

// Only the transaction arm carries an age column, and it is named for the clock
// it actually reads (spec D8). The discrepancy arm's has been withdrawn — see
// DISCREPANCY_COLUMNS. Not sortable yet — issue 10.
export const TRANSACTION_COLUMNS: readonly ReviewQueueColumn[] = [
  { label: 'Amount', track: 'minmax(116px, 0.6fr)', numeric: true },
  {
    label: 'Transaction Number',
    track: 'minmax(148px, 0.6fr)',
    tooltip:
      'The human-facing ledger number — the one to quote to finance or to a colleague. Every row in this queue has one: an unnumbered transaction is not yet a human-actionable item and EPS excludes it.',
  },
  {
    label: 'Transaction ID',
    track: 'minmax(132px, 0.7fr)',
    tooltip:
      'Our internal UUID for the transaction. Truncated — use the copy button rather than reading it off the screen. This is what the search box matches, and what a row deep-link carries.',
  },
  { label: 'Type', track: 'minmax(152px, 0.8fr)' },
  { label: 'Source', track: 'minmax(88px, 0.4fr)' },
  { label: 'Employee', track: 'minmax(148px, 0.9fr)' },
  { label: 'Insured', track: 'minmax(148px, 0.9fr)' },
  { label: 'Carrier', track: 'minmax(148px, 0.9fr)' },
  BENEFIT_ID_COLUMN,
  {
    label: 'Token',
    track: 'minmax(160px, 0.9fr)',
    tooltip:
      "The reference this card rail is looked up by — Marqeta's transaction token, or for WEX the merchant log id (which identifies the card, so it repeats across that card's rows). Every other rail (check, Group ACH, iStream, EE paid) has none, and the cell is blank: the internal transaction id used to stand in here, which meant a column of ids that look like tokens but are not.",
  },
  { label: 'Date', track: 'minmax(150px, 0.8fr)' },
  {
    label: 'Days since transaction',
    track: 'minmax(112px, 0.45fr)',
    numeric: true,
    wrap: true,
    tooltip:
      'Whole days between the transaction timestamp and now. Not sortable yet — scan it, do not rely on ordering.',
  },
  // Holds the Allocate / Post pill, the Return button, the copy-link button, and
  // — only on a row the allocator matched to a benefit — the jump-to-Benefits
  // button and the App Engine link. The track is sized for FIVE, the widest a
  // row gets: a minimum that only fits the unmatched rows would squeeze the
  // matched ones, and the two cases sit in the same column of the same table.
  { label: 'Action', track: 'minmax(280px, 0.8fr)' },
]

export const DISCREPANCY_COLUMNS: readonly ReviewQueueColumn[] = [
  { label: 'Type', track: 'minmax(140px, 0.7fr)' },
  { label: 'Employee', track: 'minmax(148px, 0.9fr)' },
  { label: 'Insured', track: 'minmax(148px, 0.9fr)' },
  { label: 'Carrier', track: 'minmax(130px, 0.8fr)' },
  BENEFIT_ID_COLUMN,
  { label: 'Company', track: 'minmax(130px, 0.8fr)' },
  {
    label: 'Expected',
    track: 'minmax(104px, 0.5fr)',
    numeric: true,
    tooltip:
      'The expected amount recorded for this benefit-month. It may be stale, and over-allocation is legitimate — so a negative Difference is not necessarily an error.',
  },
  { label: 'Paid', track: 'minmax(104px, 0.5fr)', numeric: true },
  { label: 'Difference', track: 'minmax(104px, 0.5fr)', numeric: true },
  { label: 'Coverage period', track: 'minmax(128px, 0.6fr)' },
  // No age column on this arm. It carried "Months since coverage period", which
  // went negative on future coverage months because it was a signed distance
  // from an arbitrary point rather than an age — `ReconIssueRow` has no
  // timestamp to measure one from. Issue 09 (`detectedAt` from EPS) is the real
  // fix; do not reinstate a derived stand-in for it.
  // Holds the Allocate pill, the jump-to-Benefits button and the copy-link
  // button — three controls, hence the wider track.
  { label: 'Action', track: 'minmax(188px, 0.7fr)' },
]

/** `grid-template-columns` value shared by a tab's header and its rows. */
export const TRANSACTION_GRID_TEMPLATE = gridTemplate(TRANSACTION_COLUMNS)
export const DISCREPANCY_GRID_TEMPLATE = gridTemplate(DISCREPANCY_COLUMNS)

// ── transaction-arm labels ──────────────────────────────────────────────────

// Type-column badge label — matches the filter chips so the table and the
// filter above it read the same. The two non-refund reasons are both floating
// amounts, split by why they're floating: no benefit could be resolved, vs a
// benefit resolved but auto-allocation couldn't place it.
export const REASON_BADGE_LABEL: Record<ReviewReason, string> = {
  [ReviewReason.Refund]: 'Refund',
  [ReviewReason.UnresolvedBenefit]: 'Unresolved benefit',
  [ReviewReason.AutoAllocateSkipped]: 'Auto-allocate failed',
  [ReviewReason.GroupAch]: 'Group ACH',
  [ReviewReason.Check]: 'Check',
}

export const REASON_TOOLTIP: Record<ReviewReason, string> = {
  [ReviewReason.Refund]:
    'Negative-amount tx — operator must classify against a paid charge.',
  [ReviewReason.UnresolvedBenefit]:
    'Ingest could not link this tx to any benefit. Pick a benefit manually.',
  [ReviewReason.AutoAllocateSkipped]:
    'Benefit is resolved but this tx falls outside its coverage window — no valid period to append to.',
  [ReviewReason.GroupAch]:
    'Group ACH payment — the auto-allocator never places these. Allocate it to a benefit/period at your discretion.',
  [ReviewReason.Check]:
    'Check payment — the auto-allocator never places these. Allocate it to a benefit/period at your discretion.',
}

/**
 * Primary action label (spec D11): refunds are POSTED against a paid charge;
 * everything else — including every discrepancy row — is ALLOCATED to a
 * benefit/period. "Adjust" is retired as a button label; it is the modal's name
 * leaking onto the button.
 */
export const REASON_ACTION_LABEL: Record<ReviewReason, PromisedAction> = {
  [ReviewReason.Refund]: 'Post',
  [ReviewReason.UnresolvedBenefit]: 'Allocate',
  [ReviewReason.AutoAllocateSkipped]: 'Allocate',
  [ReviewReason.GroupAch]: 'Allocate',
  [ReviewReason.Check]: 'Allocate',
}

/** Every discrepancy row is allocation work — no refund case on this arm. */
export const DISCREPANCY_ACTION_LABEL: PromisedAction = 'Allocate'

// ── discrepancy-arm labels ──────────────────────────────────────────────────

export const ISSUE_BADGE: Record<
  ReconIssueType,
  { label: string; tone: 'primary' | 'warning' | 'danger' }
> = {
  [ReconIssueType.Overpaid]: {
    label: ISSUE_TYPE_LABEL[ReconIssueType.Overpaid],
    tone: ISSUE_TYPE_TONE[ReconIssueType.Overpaid],
  },
  [ReconIssueType.Underpaid]: {
    label: ISSUE_TYPE_LABEL[ReconIssueType.Underpaid],
    tone: ISSUE_TYPE_TONE[ReconIssueType.Underpaid],
  },
  [ReconIssueType.ManualReview]: {
    label: ISSUE_TYPE_LABEL[ReconIssueType.ManualReview],
    tone: ISSUE_TYPE_TONE[ReconIssueType.ManualReview],
  },
}

// Matches the five transaction reasons, which already explain themselves.
// Copy avoids "adjust" and "review" as verbs (spec D11) — the badge label is
// "Flagged issue" rather than the wire name "Manual review", which used to be
// exempted from D11 on the grounds of being the enum's own spelling.
export const ISSUE_TOOLTIP: Record<ReconIssueType, string> = {
  [ReconIssueType.Overpaid]:
    'More was paid into this benefit-month than expected. Over-allocation is legitimate and the expected amount may be stale — check both before you change anything.',
  [ReconIssueType.Underpaid]:
    'Less was paid into this benefit-month than expected. Allocate the missing amount, or correct the expected figure.',
  [ReconIssueType.ManualReview]:
    'An operator flagged this benefit-month by hand. Paid and expected may well reconcile — the flag is why the row is here.',
}

// ── the Employee and Insured cells ──────────────────────────────────────────
//
// Two columns since EMS-10248, not one "Employee / Insured". The single cell
// ran a fallback chain — first insured, else employee, else benefit id — which
// always had something to show and so never revealed WHICH of the two you were
// reading. Split apart, each cell answers for itself and either may be empty.
// Deliberately no cross-fallback: a name under the wrong heading is worse than
// a dash under the right one.

/** A row carrying the two name fields — both arms' wire shapes qualify. */
interface NamedRow {
  employeeName: string | null
  insuredPersons: readonly { firstName: string; lastName: string }[]
}

/** The Employee cell: the employee this row was ingested against. */
export const employeeNameOf = (row: NamedRow): string | null => row.employeeName

const personName = (person: { firstName: string; lastName: string }): string =>
  `${person.firstName} ${person.lastName}`.trim()

/**
 * The Insured cell: the first insured person the row can actually name.
 *
 * Only the first one matters. An earlier version suffixed `+N` for the rest,
 * on the reasoning that a family benefit should not read as single-insured —
 * but the count named nobody, could not be acted on, and simply crowded a
 * column that exists to answer "who is this". Skipping a nameless entry rather
 * than giving up on the cell is kept: `insuredPersons[0]` is not always the
 * person with a name.
 */
export const insuredNameOf = (row: NamedRow): string | null =>
  row.insuredPersons.map(personName).find(Boolean) ?? null

/**
 * One string for a place with no pair of columns to split across — the Adjust
 * Payment modal's labels, and nothing else.
 *
 * Keeps the pre-split fallback chain deliberately: the table now refuses to let
 * one name stand in for the other, but the modal has a single line to fill and
 * a name from either side of that chain beats an empty one.
 */
export const insuredOrEmployeeName = (row: NamedRow): string | null =>
  insuredNameOf(row) ?? row.employeeName

/** As {@link insuredOrEmployeeName}, falling back to the id a discrepancy always has. */
const discrepancyName = (issue: ReconIssueRow): string =>
  insuredOrEmployeeName(issue) ?? issue.benefitId

/**
 * Label the Adjust Payment modal pins on its benefit picker for a discrepancy
 * row. Keeps the "· Carrier" suffix the table drops: the modal has no Carrier
 * column to drop it into, and its target's `carrierName` is not rendered
 * anywhere else. Issue 02 records these as deliberately separate from the
 * table's cells — splitting those into Employee and Insured must not collapse
 * this back together with them.
 */
export const discrepancyBenefitLabel = (issue: ReconIssueRow): string => {
  const name = discrepancyName(issue)
  return issue.carrierName ? `${name} · ${issue.carrierName}` : name
}

// ── age (spec D8) ───────────────────────────────────────────────────────────
//
// Two columns, two clocks, two honest names — never one "Age".
//
// `now` is a parameter rather than a `dayjs()` read inside the body so both
// stay pure and their tests cannot rot: a fixture that was "3 days ago" when it
// was written is a different number next month. Callers omit it.

/**
 * Whole days between an unposted transaction's timestamp and `now`, or null if
 * the row carries no parseable date (the cell then renders a dash).
 *
 * Truncates rather than rounds — 23h59m ago is still 0 days since, because
 * rounding up would age every row by a day the moment it landed. A future
 * timestamp reports negative rather than clamping to 0: under a header reading
 * "Days since transaction", -2 is literally true and 0 would not be. That is
 * near-unreachable for ingested transactions, and is pinned so it stays a
 * decision rather than an accident.
 */
export const daysSinceTransaction = (
  transactionDate: string | null | undefined,
  now: dayjs.Dayjs = dayjs()
): number | null => {
  if (!transactionDate) return null
  const at = dayjs(transactionDate)
  if (!at.isValid()) return null
  return now.diff(at, 'day')
}

// ── per-tab filter vocabularies (spec D7) ───────────────────────────────
//
// The two are disjoint apart from 'all', which is why filter state is per
// tab rather than shared: EPS rejects a filter that does not belong to the
// requested `kind`, so a value must never be offered on the wrong tab.

export type TransactionFilter = 'all' | `${ReviewReason}`
export type DiscrepancyFilter = 'all' | `${ReconIssueType}`

export const TRANSACTION_FILTER_OPTIONS: readonly {
  label: string
  value: TransactionFilter
}[] = [
  { label: 'All', value: 'all' },
  {
    label: REASON_BADGE_LABEL[ReviewReason.Refund],
    value: ReviewReason.Refund,
  },
  {
    label: REASON_BADGE_LABEL[ReviewReason.UnresolvedBenefit],
    value: ReviewReason.UnresolvedBenefit,
  },
  {
    label: REASON_BADGE_LABEL[ReviewReason.AutoAllocateSkipped],
    value: ReviewReason.AutoAllocateSkipped,
  },
  {
    label: REASON_BADGE_LABEL[ReviewReason.GroupAch],
    value: ReviewReason.GroupAch,
  },
  { label: REASON_BADGE_LABEL[ReviewReason.Check], value: ReviewReason.Check },
]

export const DISCREPANCY_FILTER_OPTIONS: readonly {
  label: string
  value: DiscrepancyFilter
}[] = [
  { label: 'All', value: 'all' },
  {
    label: ISSUE_BADGE[ReconIssueType.Overpaid].label,
    value: ReconIssueType.Overpaid,
  },
  {
    label: ISSUE_BADGE[ReconIssueType.Underpaid].label,
    value: ReconIssueType.Underpaid,
  },
  {
    label: ISSUE_BADGE[ReconIssueType.ManualReview].label,
    value: ReconIssueType.ManualReview,
  },
]

/**
 * Decorate a tab's chips with their row counts — "Refund (3)" — so an
 * operator can see where the work is without clicking each chip in turn.
 *
 * Only the LABEL is touched. `value` is what the URL contract and the request
 * are built from, and a decorated one would be unparseable on the way back in.
 *
 * **"All" is the SUM of this arm's counts, not the response's `total`.** The two differ
 * the moment a chip is selected: `total` is scoped to the active filter and
 * `counts` deliberately is not, so on a filtered page `total` IS the selected
 * chip's count — "All (3)" sitting beside "Refund (3)". The sum is what "All"
 * actually means here: every row of this `kind` matching the search, whatever
 * the filter. (`total` keeps its own job, pagination, over in <QueuePanel>.)
 *
 * An ABSENT — or entirely EMPTY — map leaves every label bare rather than
 * reading "(0)" across the board: no `counts` on the response means a backend
 * that predates them, or a failed request — never an empty queue. A missing
 * KEY inside an otherwise-populated map is the other case and does zero-fill;
 * EPS sends every key of the requested kind even at zero, so that is already a
 * contract violation, but "(0)" is the closest true thing to render.
 *
 * Counts move with `kind` and the applied search, so they shift as the operator
 * searches. The SUB-TAB labels above them deliberately do NOT — those are
 * unfiltered grand totals (see `useReviewQueueCount`). Two different numbers on
 * one screen, on purpose; do not "fix" the tab labels to agree with these.
 */
export const withChipCounts = <F extends ReviewQueueFilter>(
  options: readonly { label: string; value: F }[],
  counts: ReviewQueueCounts | undefined
): readonly { label: string; value: F }[] => {
  // An EMPTY map takes the bare branch too, not the zero-filling one. The EPS
  // schema is `.partial()` on all eight keys, so `{}` is a legal response
  // shape, and it is far likelier to mean "this backend sent nothing useful"
  // than "all eight buckets are genuinely empty" — which is the same lie the
  // absent-map branch exists to avoid telling.
  if (!counts || Object.keys(counts).length === 0) return options
  // Typed as the full filter union rather than `F` so that comparing against
  // 'all' NARROWS the else branch to a countable key. A bare generic would
  // stay `F` there and not be indexable.
  const bucket = (value: ReviewQueueFilter): number =>
    // 'all' is not a bucket the backend counts, so it contributes nothing —
    // which is what lets the sum below just fold over every option.
    value === 'all' ? 0 : (counts[value] ?? 0)
  // Summed over the OPTIONS this tab offers, not over every key in the
  // map. `options` is the arm's own vocabulary; a map that arrived carrying
  // the other arm's keys as well (a request that omitted `kind`) must not
  // inflate this tab's "All". The mirror of the zero-fill rule above:
  // absent key means "not of this kind", so a foreign key present means the
  // same thing and is likewise not ours to count.
  const sum = options.reduce<number>(
    (running, option) => running + bucket(option.value),
    0
  )
  const countFor = (value: ReviewQueueFilter): number =>
    value === 'all' ? sum : bucket(value)
  return options.map((option) => ({
    ...option,
    label: `${option.label} (${countFor(option.value)})`,
  }))
}

// ── copy ────────────────────────────────────────────────────────────────────

/**
 * The strings one arm's panel renders. No tab label here any more — the labels
 * and their counts live on the page's tab strip since the arms became top-level
 * tabs (EMS-10248).
 */
interface QueueTabCopy {
  emptyDescription: string
  /**
   * Kept to the fields EPS actually matches. The transaction arm also matches
   * benefit id and the discrepancy arm insured-person names, but NEITHER
   * matches the card token or the carrier name — do not widen this to
   * promise them.
   */
  searchPlaceholder: string
  errorMessage: string
}

// Operator-facing wording for the two arms. The LABELS are "Unallocated
// payments" and "Discrepancies" (CONTEXT.md), so the error and empty states say
// that too — copy naming a tab the operator cannot see sends them looking for
// it. "Flagged issues" is no longer available for this arm's copy: it names one
// of the three issue TYPES on it. The domain words survive in the identifiers
// around this, on purpose: the rename is UI-only (spec D11).
export const TRANSACTION_COPY: QueueTabCopy = {
  emptyDescription: 'Nothing to allocate — the auto-allocator is caught up.',
  searchPlaceholder: 'Search by transaction id or employee',
  errorMessage: 'Failed to load unallocated payments',
}

export const DISCREPANCY_COPY: QueueTabCopy = {
  emptyDescription: 'No discrepancies.',
  searchPlaceholder: 'Search by employee or benefit id',
  errorMessage: 'Failed to load discrepancies',
}

/** Cell placeholder for an absent value. */
export const DASH = '—'

/**
 * The one explanation behind every source-reported value's warning icon. It
 * does not say WHY the transaction is unresolved — a wrong id in the file and a
 * right id naming an excluded benefit look the same from here — only that the
 * value is the file's word, not ours.
 */
export const SOURCE_REPORTED_WARNING =
  'Reported by iStream — not matched to an active benefit. Verify before allocating.'
